// Custom calendar date picker (no <input type=date>, no library). Month grid
// with prev/next navigation, today + selected highlighted.
//
// Which days are pickable depends on what the date MEANS. Scheduling a meeting
// cannot happen in the past; filtering history cannot happen in the future. So
// the direction is a prop rather than a rule baked into the calendar.
// Value + onChange use YYYY-MM-DD (local).

import { memo, MouseEvent, useCallback, useMemo, useState } from 'react';

import { IconChevronLeft, IconChevronRight } from '../ui/icons';

const WD = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

const toISO = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const midnight = (d: Date) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

/** Which way time runs for this picker. */
export type Direction = 'future' | 'past';

export const DatePicker = memo(function DatePicker({
  value,
  onChange,
  direction = 'future',
}: {
  value: string;
  onChange: (v: string) => void;
  /** 'future' (scheduling, the default) or 'past' (filtering what happened). */
  direction?: Direction;
}) {
  const selected = value ? new Date(value + 'T00:00:00') : null;
  const [view, setView] = useState(() => {
    const base = selected ?? new Date();
    return new Date(base.getFullYear(), base.getMonth(), 1);
  });
  const today = midnight(new Date());

  const prev = useCallback(() => setView((v) => new Date(v.getFullYear(), v.getMonth() - 1, 1)), []);
  const next = useCallback(() => setView((v) => new Date(v.getFullYear(), v.getMonth() + 1, 1)), []);
  const pick = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      const d = e.currentTarget.dataset.d;
      if (d) onChange(d);
    },
    [onChange],
  );

  const cells = useMemo(() => {
    const first = new Date(view.getFullYear(), view.getMonth(), 1);
    const start = new Date(first);
    start.setDate(1 - first.getDay()); // back up to the Sunday of the first week
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d;
    });
  }, [view]);

  const selISO = selected ? toISO(selected) : '';
  const todayISO = toISO(today);
  const sameMonthAsToday =
    view.getFullYear() === today.getFullYear() && view.getMonth() === today.getMonth();
  // Do not let anyone page into a month where every day is disabled.
  const prevDisabled = direction === 'future' && sameMonthAsToday;
  const nextDisabled = direction === 'past' && sameMonthAsToday;

  return (
    <div className="bz-dp">
      <div className="bz-dp-head">
        <button
          type="button"
          className="bz-dp-nav"
          onClick={prev}
          disabled={prevDisabled}
          aria-label="Previous month"
        >
          <IconChevronLeft size={16} />
        </button>
        <span className="bz-dp-month">
          {MONTHS[view.getMonth()]} {view.getFullYear()}
        </span>
        <button
          type="button"
          className="bz-dp-nav"
          onClick={next}
          disabled={nextDisabled}
          aria-label="Next month"
        >
          <IconChevronRight size={16} />
        </button>
      </div>
      <div className="bz-dp-grid bz-dp-wd">
        {WD.map((w, i) => (
          <span key={i} className="bz-dp-wdlabel">
            {w}
          </span>
        ))}
      </div>
      <div className="bz-dp-grid">
        {cells.map((d) => {
          const iso = toISO(d);
          const cls = ['bz-dp-day'];
          if (d.getMonth() !== view.getMonth()) cls.push('bz-dp-day-out');
          if (iso === selISO) cls.push('bz-dp-day-sel');
          else if (iso === todayISO) cls.push('bz-dp-day-today');
          return (
            <button
              key={iso}
              type="button"
              className={cls.join(' ')}
              data-d={iso}
              onClick={pick}
              disabled={direction === 'past' ? d > today : d < today}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
});
